import Text, { RawText } from "@/comps/text"

import {
  Desktop,
  Mobile,
  Tablet,
} from "@/utils/breakpoints"
import { FlexBox, FlexItem } from "react-styled-flex"

import styled from "styled-components"
import { z } from "zod"
import Bubble from "../bubble"
import { ResponsiveImage } from "../image"

const Possibility = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  flex: 1;

  & + & {
    border-left: solid 1px var(--color-separator);
  }
`

export const achievementSchema = z.object({
  component: z.literal("achievements"),
  actions: z.array(
    z.object({
      tablet: z.boolean().optional(),
      action: z.string(),
      value: z.string(),
      unit: z.string(),
    })
  ),
})

type Achievement = z.infer<
  typeof achievementSchema
>["actions"][number]

const Achievement = (props: Achievement) => {
  const { tablet, action, value, unit } = props

  return (
    <Possibility className="z-20 bg-color-cell first:rounded-l-3xl last:rounded-r-3xl">
      <FlexItem box shrink={0} paddingTop={7}>
        <Text
          content={action}
          size="18"
          weight="700"
          capitalization="uppercase"
        />
      </FlexItem>
      <FlexItem box shrink={0}>
        <RawText
          content={value}
          size={tablet ? "32" : "36"}
          weight="800"
        />
      </FlexItem>
      <FlexItem box shrink={0} paddingBottom={7}>
        <Text
          content={unit}
          size="16"
          weight="400"
          color="600"
          capitalization="capitalize"
        />
      </FlexItem>
    </Possibility>
  )
}

const PossibilityMobile = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  flex: 1;

  & + & {
    border-top: solid 1px var(--color-separator);
  }
`

const AchievementMobile = (props: Achievement) => {
  const { action, value, unit } = props

  return (
    <PossibilityMobile className="z-20 bg-color-cell first:rounded-t-3xl last:rounded-b-3xl">
      <FlexBox column margin={20}>
        <FlexItem box shrink={0}>
          <Text
            content={action}
            size="18"
            weight="700"
            capitalization="uppercase"
          />
        </FlexItem>
        <FlexItem box shrink={0}>
          <Text
            content={unit}
            size="16"
            weight="400"
            color="600"
            capitalization="capitalize"
          />
        </FlexItem>
      </FlexBox>
      <FlexItem box shrink={0} margin={20}>
        <RawText content={value} size="28" weight="800" />
      </FlexItem>
    </PossibilityMobile>
  )
}

const ContainerDesktop = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;

  align-items: stretch;

  width: 1000px;
  height: 144px;

  border-radius: 24px;

  background-color: var(--color-background-cell);
`

const ContainerTablet = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;

  align-items: stretch;

  width: 728px;
  height: 144px;

  border-radius: 24px;

  background-color: var(--color-background-cell);
`

const ContainerMobile = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;

  align-items: stretch;

  width: calc(var(--w-screen) - 32px);
  margin: 0 16px;

  border-radius: 12px;

  background-color: var(--color-background-cell);
`
type AllAchievementsProps = z.infer<
  typeof achievementSchema
>
const AllAchievements = (props: AllAchievementsProps) => {
  const { actions } = props

  return (
    <>
      <div className="relative py-[35px] tablet:py-[40px] desktop:py-[55px]">
        <div className="pointer-events-none absolute right-0 top-0 z-10 w-full -translate-y-1/3 overflow-hidden">
          <ResponsiveImage
            src="/feature/background"
            className="w-full object-fill dark:opacity-40"
          />
        </div>
        <div className="z-20 flex w-full flex-col items-center justify-center gap-[22px] tablet:gap-10 desktop:gap-6">
          <div className="flex flex-row justify-center">
            <Bubble
              text="your achievements"
              color="purplePink"
            />
          </div>
          <Desktop>
            <ContainerDesktop>
              {actions.map((achievement) => (
                <Achievement
                  key={achievement.action}
                  {...achievement}
                />
              ))}
            </ContainerDesktop>
          </Desktop>

          <Tablet>
            <ContainerTablet>
              {actions.map((achievement) => (
                <Achievement
                  key={achievement.action}
                  tablet
                  {...achievement}
                />
              ))}
            </ContainerTablet>
          </Tablet>

          <Mobile>
            <ContainerMobile>
              {actions.map((achievement) => (
                <AchievementMobile
                  key={achievement.action}
                  tablet
                  {...achievement}
                />
              ))}
            </ContainerMobile>
          </Mobile>
        </div>
      </div>
    </>
  )
}

export default AllAchievements
